import staticDemoScreenshotUrl from "../../../assets/static-demo-screenshot.svg";
import { buildAnalyticsModel, processInstances } from "../components/analytics/utils";
import { caseInBounds, processObservations } from "./mocks/demo_metrics";
import { median } from "../components/processes/processDurationStats";
import type { PaginatedResponse } from "./auto-types/PaginatedResponse";
import { demoFilterResponses } from "./mocks/demo_filters";

// Replies ride the same alias as the clients, so product builds exclude the demo.
import { demoScopedAnswer } from "./mocks/demo_chat_answers";
import { demoProcessSummaries } from "./mocks/demo_processes";
import { demoRecordings } from "./mocks/demo_recordings";
import { buildDemoDataset } from "./mocks/demo_dataset";
import { demoUsers, demoWhoami } from "./mocks/demo_users";
import { demoAlertReviews } from "./mocks/demo_watchlist";
import { ALL_PROCESSES, processesData } from "./mocks/processes";
import { recordingBdr } from "./mocks/recording_bdr";
import { recordingPipelineGeneration } from "./mocks/recording_pipeline";
import type { PersonProcessStats, Recording } from "./openapi-aliases";
import type { components as CompanyRouterComponents } from "./openapi/company-router";
import type { components as CoreComponents } from "./openapi/core";

type RouterUser = CompanyRouterComponents["schemas"]["User"];
type Company = CompanyRouterComponents["schemas"]["Company"];
type ClankPermissions = CompanyRouterComponents["schemas"]["ClankPermissions"];
type ExposedConfig = CoreComponents["schemas"]["ExposedConfig"];
type RecordingOsPermissionsPayload = CoreComponents["schemas"]["RecordingOsPermissionsPayload"];
type StatusPayload = CoreComponents["schemas"]["StatusPayload"];
type UpdateStatus = CoreComponents["schemas"]["UpdateStatus"];
type VocabularyCatalogue = CompanyRouterComponents["schemas"]["VocabularyCatalogue"];

const now = "2026-08-03T00:00:00Z";

const okResponse = new Response(null, { status: 200 });

const page = <T, Extra = null>(items: T[], extra: Extra): PaginatedResponse<T, Extra> => ({
  items,
  page: 1,
  page_size: 50,
  total_items: items.length,
  total_pages: 1,
  extra,
});

// The original sample persona: owner of the hand-written fixtures (the BDR
// recordings and their processes), kept alongside the exported demo dataset.
const anonymizedBdr: RouterUser = {
  _updated_at: now,
  company_id: demoWhoami.company_id,
  date_created: now,
  early_adopter: "releases",
  email: "anonymized.bdr@example-company.com",
  external_auth_issuer: null,
  external_auth_subject: null,
  first_name: "Anonymized",
  last_name: "Bdr",
  id: "00068000-11fb-8302-8c10-1a913601212b",
  prompt: "",
  last_login: now,
  username: "anonimized.bdr",
};

const allUsers: RouterUser[] = [...demoUsers, anonymizedBdr];

// The visitor's own identity: the BDR persona, so the demo opens on the same
// default view it always had; the journey users are reached via the gallery
// (or the user filter).
const coreUser = anonymizedBdr;
const routerUser: RouterUser = coreUser;

const company: Company = {
  _updated_at: now,
  allow_password_login: true,
  id: demoWhoami.company_id ?? 1,
  logo_url: null,
  name: "Static Demo Company",
  prompt: "",
};

const managerPermissions: ClankPermissions = {
  is_admin: false,
  processes: { visualization: "AllCompany" },
  recordings: { visualization: "AllCompany" },
  screenshots: { visualization: "AllCompany" },
  users: { creation: true, visualization: "AllCompany" },
};

const config: ExposedConfig = {
  company_router_url: "",
  version: "static-demo",
};

const recordingStatus: StatusPayload = {
  current_recording_dir: null,
  elapsed_seconds: 0,
  ok: true,
  started_at: null,
  status: "idle",
  sync_status: { status: "idle" },
  pending_review: 0,
};

const recordingOsPermissions: RecordingOsPermissionsPayload = {
  permissions: [
    { kind: "accessibility", granted: true, required: true },
    { kind: "input_monitoring", granted: true, required: true },
    { kind: "screen_and_system_audio_recording", granted: true, required: true },
  ],
};

const updateStatus: UpdateStatus = {
  currentVersion: "static-demo",
  enabled: false,
  lastCheckedAt: null,
  latest: null,
  supported: false,
  updateManifestUrl: "",
};

// Assemble all views from the same split and dated snapshot. The large analysis
// payloads remain in lazy chunks and the result is reused for this page load.
let dataset: Promise<ReturnType<typeof buildDemoDataset>> | undefined;
export const loadDemoDataset = () => {
  dataset ??= Promise.all([
    import("./mocks/demo_process_details"),
    import("./mocks/demo_recording_details"),
  ]).then(([{ demoProcessDetails }, { demoRecordingDetails }]) =>
    buildDemoDataset(
      [...Object.values(demoProcessDetails), ...ALL_PROCESSES],
      [...Object.values(demoRecordingDetails), recordingBdr, recordingPipelineGeneration],
      new Set(demoRecordings.map(recording => recording.id)),
    ),
  );
  return dataset;
};

export const resolveDemoInstanceIds = async (ids: string[]) => {
  const data = await loadDemoDataset();
  return ids.map(id => data.instanceIds.get(id) ?? id);
};

export const demoChatAnswerFor = async (
  filters: CompanyRouterComponents["schemas"]["ChatScopeFilters"],
  text: string,
) => demoScopedAnswer((await loadDemoDataset()).processes, filters, text);

// The fixture processes all belong to the BDR persona.
const allProcessSummaries = [...demoProcessSummaries, ...processesData];

const vocabularyCatalogue: VocabularyCatalogue = {
  base: [],
  contributed: [],
};

// A resolver may answer with a promise (the heavyweight fixtures are behind
// dynamic imports, so their chunks download only when first asked for).
const dataResponse = async <T>(data: T | Promise<T>) => ({
  data: await data,
  response: okResponse,
});

const ok = () =>
  Promise.resolve({
    response: okResponse,
  });

const byModel = (model?: string) => {
  switch (model) {
    case "user":
      return page<RouterUser>(allUsers, null);
    case "company":
      return page<Company>([company], null);
    default:
      return page<never>([], null);
  }
};

const coreData = (method: string, path: string, options?: unknown): unknown => {
  switch (`${method} ${path}`) {
    case "GET /api/config/":
      return config;
    case "GET /api/whoami/":
      return coreUser;
    case "GET /api/recording/status/":
    case "POST /api/recording/{cmd}/":
    case "POST /api/recording/sync/":
    case "POST /api/recording/review/{recording}/approve/":
    case "DELETE /api/recording/review/{recording}/":
      return recordingStatus;
    case "GET /api/recording/review/":
      return { recordings: [] };
    case "GET /api/recording/review/{recording}/screenshots/":
      return { screenshots: [] };
    case "GET /api/recording/settings/":
      return { review_before_upload: false };
    case "PUT /api/recording/settings/":
      return (options as { body?: unknown } | undefined)?.body;
    case "GET /api/recording/screenshot-format/":
    case "PUT /api/recording/screenshot-format/":
      return { screenshot_format: "webp" };
    case "GET /api/recording/os-permissions/":
    case "POST /api/recording/os-permissions/{permission}/request/":
    case "POST /api/recording/os-permissions/{permission}/open-settings/":
      return recordingOsPermissions;
    case "GET /api/update/status/":
    case "POST /api/update/check/":
    case "POST /api/update/download/":
    case "POST /api/update/apply/":
      return updateStatus;
    case "GET /api/workflows/":
    case "GET /api/ran-workflows/":
    case "GET /api/workflow-versions/":
      return page<never>([], null);
    case "POST /api/workflow/run/":
      return null;
    default:
      console.warn("Unhandled static demo core API call", method, path, options);
      return {};
  }
};

type AlertDb = CompanyRouterComponents["schemas"]["AlertDb"];
type AlertForm = CompanyRouterComponents["schemas"]["AlertForm"];

// Watches saved during a demo session, so a save is visible in the list.
// In-memory only: gone on reload, like everything else here.
const createdAlerts: AlertDb[] = [];
let alertSeq = 0;

const createAlert = (form: AlertForm | undefined): AlertDb => {
  alertSeq += 1;
  const alert: AlertDb = {
    _updated_at: now,
    cancelled: false,
    created_at: now,
    creator_id: routerUser.id,
    edited_at: now,
    enabled: form?.enabled ?? true,
    id: `00000000-0000-7000-8000-${String(alertSeq).padStart(12, "0")}`,
    payload: {
      version: "1",
      prompt: form?.prompt ?? "",
      schedule: form?.schedule ?? null,
      scope: form?.scope ?? { user_ids: [], process_slugs: [], window_days: 30, timezone: "UTC" },
      title: form?.title ?? "",
    },
    periodicity: form?.periodicity ?? "periodic",
  };
  createdAlerts.push(alert);
  return alert;
};

const filterDemoRecordings = (
  recordings: Recording[],
  query?: {
    user_id_list?: string;
    datetime_gte?: string;
    datetime_lte?: string;
    include_in_processes?: boolean;
  },
) =>
  recordings.filter(
    recording =>
      (query?.user_id_list === undefined ||
        query.user_id_list.split(",").includes(recording.user_id)) &&
      (!query?.datetime_gte ||
        Date.parse(recording.date_started) >= Date.parse(query.datetime_gte)) &&
      (!query?.datetime_lte ||
        Date.parse(recording.date_started) <= Date.parse(query.datetime_lte)) &&
      (query?.include_in_processes === undefined ||
        recording.include_in_processes === query.include_in_processes),
  );

/** The process list for `users` (everyone when absent) within `bounds`, as the API sums it. */
const listDemoProcesses = (
  users: string[] | undefined,
  bounds: { datetime_gte?: string; datetime_lte?: string },
) =>
  loadDemoDataset().then(data => {
    const details = new Map(data.processes.map(process => [process.slug, process]));
    return allProcessSummaries.flatMap(summary => {
      const detail = details.get(summary.slug);
      if (!detail) return [];
      const instances = detail.instances.filter(
        instance =>
          (!users || users.includes(instance.source.userId)) &&
          caseInBounds(instance.source, bounds),
      );
      if (!instances.length) return [];
      const observations = processObservations({ ...detail, instances });
      const totalDuration = observations.reduce((sum, observation) => sum + observation.seconds, 0);
      const appSeconds: Record<string, number> = {};
      const people = new Map<string, PersonProcessStats>();
      observations.forEach((observation, index) => {
        const person = people.get(observation.userId) ?? {
          userId: observation.userId,
          runSeconds: [],
          stepCount: 0,
          firstSeen: observation.traceStart,
          lastSeen: observation.traceEnd,
          appSeconds: {},
        };
        person.runSeconds.push(observation.seconds);
        person.stepCount += instances[index].steps.length;
        if (observation.traceStart < person.firstSeen) person.firstSeen = observation.traceStart;
        if (observation.traceEnd > person.lastSeen) person.lastSeen = observation.traceEnd;
        for (const [app, seconds] of Object.entries(observation.appSeconds ?? {})) {
          appSeconds[app] = (appSeconds[app] ?? 0) + seconds;
          person.appSeconds[app] = (person.appSeconds[app] ?? 0) + seconds;
        }
        people.set(observation.userId, person);
      });
      return [
        {
          ...summary,
          stepNames: [
            ...new Set(
              instances.flatMap(instance =>
                instance.steps
                  .map(step => detail.nodes.find(node => node.id === step.nodeId)?.name)
                  .filter((name): name is string => !!name),
              ),
            ),
          ].sort(),
          appSeconds,
          frequency: instances.length,
          avgDuration: totalDuration / instances.length,
          totalDuration,
          medianDuration: median(
            observations.map(observation => observation.seconds).sort((a, b) => a - b),
          ),
          people: [...people.values()].sort((a, b) => a.userId.localeCompare(b.userId)),
          apps: [...new Set(observations.flatMap(observation => observation.apps))].sort(),
          firstSeen: instances.map(instance => instance.source.traceStart).sort()[0],
          lastSeen: instances
            .map(instance => instance.source.traceEnd)
            .sort()
            .at(-1)!,
          stepCount: instances.reduce((sum, instance) => sum + instance.steps.length, 0),
        },
      ];
    });
  });

const routerData = (method: string, path: string, options?: any): unknown => {
  switch (`${method} ${path}`) {
    case "GET /api/whoami/":
      return routerUser;
    case "GET /api/clank/permissions/":
      return managerPermissions;
    case "GET /api/clank/user/{user_id}/permissions/":
    case "PUT /api/clank/user/{user_id}/permissions/":
      return managerPermissions;
    case "GET /api/clank/sync/{model}/":
      return byModel(options?.params?.path?.model);
    case "GET /api/clank/recordings/overview/": {
      const query = options?.params?.query;
      return loadDemoDataset().then(data => {
        const counts = new Map<string, number>();
        for (const recording of filterDemoRecordings(data.recordings, {
          ...query,
          include_in_processes: true,
        })) {
          counts.set(recording.user_id, (counts.get(recording.user_id) ?? 0) + 1);
        }
        const people = [...counts]
          .map(([user_id, recordings]) => ({ user_id, recordings }))
          .sort((a, b) => b.recordings - a.recordings || a.user_id.localeCompare(b.user_id));
        return { recordings: people.reduce((sum, person) => sum + person.recordings, 0), people };
      });
    }
    case "GET /api/clank/recordings/": {
      const query = options?.params?.query;
      return loadDemoDataset().then(data => {
        const recordings = filterDemoRecordings(data.recordings, query);
        const durations = recordings.map(
          recording =>
            (Date.parse(recording.date_ended!) - Date.parse(recording.date_started)) / 1000,
        );
        const pageSize = query?.page_size ?? 50;
        const totalPages = Math.max(1, Math.ceil(recordings.length / pageSize));
        const pageNumber = Math.min(Math.max(1, query?.page ?? 1), totalPages);
        return {
          ...page(recordings.slice((pageNumber - 1) * pageSize, pageNumber * pageSize), {
            max_duration_s: durations.length ? Math.max(...durations) : 0,
            min_duration_s: durations.length ? Math.min(...durations) : 0,
          }),
          page: pageNumber,
          page_size: pageSize,
          total_items: recordings.length,
          total_pages: totalPages,
        };
      });
    }
    case "GET /api/clank/recordings/summaries/": {
      const ids: string[] = options?.params?.query?.recording_id_list?.split(",") ?? [];
      return loadDemoDataset().then(data =>
        ids.flatMap(id => {
          const status = data.details[id]?.status;
          if (status?.status !== "completed") return [];
          return [
            {
              recording_id: id,
              app_seconds: Object.fromEntries(
                buildAnalyticsModel(status.result)
                  .appUsage.filter(app => app.totalMin > 0)
                  .map(app => [app.name, app.totalMin * 60]),
              ),
              process_count: new Set(processInstances(status.result).map(node => node.name)).size,
            },
          ];
        }),
      );
    }
    case "GET /api/clank/recordings/{recording_id}/":
      return loadDemoDataset().then(
        data => data.details[options?.params?.path?.recording_id] ?? {},
      );
    case "GET /api/clank/recordings/{recording_id}/screenshot/{screenshot_file}":
      return { method: "GET", url: staticDemoScreenshotUrl };
    case "GET /api/clank/processes/": {
      const query = options?.params?.query ?? {};
      return listDemoProcesses(query.user_id_list?.split(","), query);
    }
    case "GET /api/clank/processes/{slug}/":
      return loadDemoDataset().then(
        data => data.processes.find(process => process.slug === options?.params?.path?.slug) ?? {},
      );
    case "POST /api/clank/processes/{slug}/filter/": {
      const answer = demoFilterResponses[options?.params?.path?.slug];
      return answer
        ? resolveDemoInstanceIds(answer.instanceIds).then(instanceIds => ({
            ...answer,
            instanceIds,
          }))
        : { explanation: null, instanceIds: [] };
    }
    case "GET /api/clank/alerts/":
      return createdAlerts;
    case "GET /api/clank/alerts/{alert_id}/runs/":
      return [];
    case "POST /api/clank/alerts/":
      return createAlert(options?.body);
    case "PUT /api/clank/alerts/{alert_id}/": {
      const existing = createdAlerts.find(alert => alert.id === options?.params?.path?.alert_id);
      if (!existing) return createAlert(options?.body);
      const form: AlertForm | undefined = options?.body;
      existing.payload = {
        version: "1",
        prompt: form?.prompt ?? "",
        schedule: form?.schedule ?? null,
        scope: form?.scope ?? { user_ids: [], process_slugs: [], window_days: 30, timezone: "UTC" },
        title: form?.title ?? "",
      };
      existing.enabled = form?.enabled ?? existing.enabled;
      return existing;
    }
    case "DELETE /api/clank/alerts/{alert_id}/": {
      const index = createdAlerts.findIndex(alert => alert.id === options?.params?.path?.alert_id);
      if (index !== -1) createdAlerts.splice(index, 1);
      return undefined;
    }
    case "POST /api/clank/alerts/{alert_id}/runs/seen/":
      return undefined;
    case "POST /api/clank/alerts/review/": {
      // A real round of the review model, canned: the first call gets the
      // clarifying questions; once the wizard has answers (a non-empty
      // clarification transcript) the review comes back settled.
      const body = options?.body;
      const slug: string = body?.context?.process_slugs?.[0] ?? "";
      const canned = demoAlertReviews[slug];
      if (!canned) {
        const asked = (body?.transcript ?? []).filter(
          (message: { role: string; content: string }) => message.role === "user",
        );
        const prompt = asked.length > 0 ? asked[asked.length - 1].content : (body?.prompt ?? "");
        return {
          title:
            slug === "customer-service-case-resolution" ? "Knowledge Base Gaps" : "Custom team KPI",
          prompt,
          questions: [],
          scope: {
            user_ids: body?.context?.user_ids ?? [],
            process_slugs: body?.context?.process_slugs ?? [],
            window_days: body?.context?.window_days ?? 30,
            schedule_every_days: body?.context?.schedule_every_days ?? 7,
          },
        };
      }
      const clarified = (body?.transcript?.length ?? 0) > 0;
      return {
        ...canned,
        questions: clarified ? [] : canned.questions,
        // The captured review predates process-scoped KPIs. Retain the setup
        // context so the next review round still resolves this definition.
        scope: {
          user_ids: body?.context?.user_ids ?? canned.scope?.user_ids,
          process_slugs: body?.context?.process_slugs ?? [slug],
          window_days: body?.context?.window_days ?? canned.scope?.window_days,
          schedule_every_days:
            body?.context?.schedule_every_days ?? canned.scope?.schedule_every_days,
        },
      };
    }
    case "GET /api/clank/vocabulary/":
      return vocabularyCatalogue;
    case "GET /api/clank/vocabulary/entries/":
    case "GET /api/clank/vocabulary/categories/":
      return [];
    case "POST /api/clank/user/":
    case "PUT /api/clank/user/{user_id}/":
      return routerUser;
    case "POST /api/clank/company/":
      return {
        _updated_at: now,
        prompt: "",
        ...options?.body,
      };
    case "PUT /api/clank/company/{company_id}/":
      return {
        _updated_at: now,
        id: options?.params?.path?.company_id,
        prompt: "",
        ...options?.body,
      };
    case "POST /api/clank/vocabulary/entries/":
    case "PUT /api/clank/vocabulary/entries/{entry_id}/":
      return {
        _updated_at: now,
        created_at: now,
        id: "00000000-0000-7000-8000-000000000010",
        normalised_process_name: "demo",
        payload: { process: "Demo", scope: "user", steps: [] },
        user_id: routerUser.id,
      };
    case "DELETE /api/clank/recordings/{recording_id}/":
    case "DELETE /api/clank/vocabulary/entries/{entry_id}/":
      return undefined;
    case "POST /api/clank/recordings/{recording_id}/analyze/":
    case "DELETE /api/clank/recordings/{recording_id}/analyze/":
      return { status: "idle" };
    default:
      console.warn("Unhandled static demo company-router API call", method, path, options);
      return {};
  }
};

const client = (resolver: (method: string, path: string, options?: unknown) => unknown) => ({
  GET: (path: string, options?: unknown) => dataResponse(resolver("GET", path, options)),
  POST: (path: string, options?: unknown) => dataResponse(resolver("POST", path, options)),
  PUT: (path: string, options?: unknown) => dataResponse(resolver("PUT", path, options)),
  PATCH: (path: string, options?: unknown) => dataResponse(resolver("PATCH", path, options)),
  DELETE: (path: string, options?: unknown) => {
    const data = resolver("DELETE", path, options);
    return data === undefined ? ok() : dataResponse(data);
  },
});

export const staticDemoCoreClient = client(coreData);
export const createStaticDemoCompanyRouterClient = () => client(routerData);
